/**
 * assets/search-modal.js
 * -----------------------------------------------------------------------
 * Powers sections/search-modal.liquid. Same DOM serves desktop dropdown
 * and mobile sheet; CSS handles the visual/order differences.
 *
 * Data flow:
 *  - Empty query  -> Liquid-rendered defaults are left alone (already in
 *                    the DOM: popular searches, default collections list,
 *                    help list, best-sellers grid).
 *  - Typing       -> Shopify predictive search REST API
 *                    (/search/suggest.json) drives Products + Suggestions.
 *                    Collections + Help are matched client-side against
 *                    the small JSON index Liquid embedded in the page,
 *                    because predictive search doesn't return product
 *                    counts or help/blog content in the shape this design
 *                    needs (see section file's comment).
 *
 * Integration:
 *  - Exposes window.toggleSearch() / window.SearchModal.{open,close,toggle}
 *    for theme's existing directive system (custom:click="toggleSearch")
 *    or for calling manually (e.g. a keyboard shortcut).
 *  - Also self-binds to [data-search-toggle] as a fallback so the icon
 *    works even without the directive system wiring it up.
 */
(function () {
  'use strict';

  var DEBOUNCE_MS = 150;
  var root = document.getElementById('SearchModal');
  if (!root) return;

  var panel = root.querySelector('[data-search-panel]');
  var input = root.querySelector('[data-search-input]');
  var clearButtons = root.querySelectorAll('[data-search-clear]');
  var closeButtons = root.querySelectorAll('[data-search-close]');
  var cancelButton = root.querySelector('[data-search-cancel]');

  var productsGrid = root.querySelector('[data-products-grid]');
  var noResults = root.querySelector('[data-no-results]');
  var noResultsHeading = noResults.querySelector('.search-modal__no-results-heading');
  var resultsEyebrow = root.querySelector('[data-results-eyebrow]');
  var resultsLink = root.querySelector('[data-results-link]');

  var suggestionsGroup = root.querySelector('[data-suggestions-group]');
  var suggestionsEyebrow = root.querySelector('[data-suggestions-eyebrow]');
  var suggestionsList = root.querySelector('[data-suggestions-list]');

  var collectionsGroup = root.querySelector('[data-collections-group]');
  var collectionsList = root.querySelector('[data-collections-list]');

  var helpGroup = root.querySelector('[data-help-group]');
  var helpList = root.querySelector('[data-help-list]');

  var collectionsIndexEl = document.querySelector('[data-search-collections-index]');
  var helpIndexEl = document.querySelector('[data-search-help-index]');
  var collectionsIndex = safeParseJSON(collectionsIndexEl) || [];
  var helpIndex = safeParseJSON(helpIndexEl) || [];

  // Snapshot the empty-state markup so we can restore it instantly
  // instead of re-fetching when the query is cleared back to "".
  var emptyState = {
    productsHTML: productsGrid.innerHTML,
    collectionsHTML: collectionsList.innerHTML,
    helpHTML: helpList.innerHTML,
    resultsEyebrowText: resultsEyebrow.textContent,
    resultsLinkHTML: resultsLink.innerHTML,
    suggestionsEyebrowText: suggestionsEyebrow.textContent
  };

  var lastFocusedEl = null;
  var debounceTimer = null;
  var activeFetchController = null;

  function safeParseJSON(el) {
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  // ---------------------------------------------------------------------
  // Open / close
  // ---------------------------------------------------------------------
  function open() {
    lastFocusedEl = document.activeElement;
    root.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    syncToggleButtons(true);
    // Wait a frame so hidden->visible transition + autofocus feels right
    window.requestAnimationFrame(function () {
      input.focus();
    });
    document.addEventListener('keydown', onKeydown, true);
  }

  function close(options) {
    options = options || {};
    root.hidden = true;
    document.documentElement.style.overflow = '';
    syncToggleButtons(false);
    document.removeEventListener('keydown', onKeydown, true);

    if (options.clearQuery) {
      input.value = '';
      resetToEmptyState();
    }

    if (lastFocusedEl && typeof lastFocusedEl.focus === 'function') {
      lastFocusedEl.focus();
    }
  }

  function toggle() {
    if (root.hidden) open(); else close();
  }

  function syncToggleButtons(isOpen) {
    document.querySelectorAll('[data-search-toggle]').forEach(function (btn) {
      btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });
  }

  function onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }
    if (event.key === 'Tab') trapFocus(event);
  }

  function trapFocus(event) {
    var focusable = panel.querySelectorAll(
      'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  // Global open/close hooks for the theme's directive system and for
  // the plain data-attribute fallback on the icon.
  window.toggleSearch = toggle;
  window.SearchModal = { open: open, close: close, toggle: toggle };

  document.addEventListener('click', function (event) {
    var toggleEl = event.target.closest('[data-search-toggle]');
    if (toggleEl) { event.preventDefault(); toggle(); return; }

    var closeEl = event.target.closest('[data-search-close]');
    if (closeEl) {
      event.preventDefault();
      close({ clearQuery: !!closeEl.hasAttribute('data-search-cancel') });
      return;
    }

    var clearEl = event.target.closest('[data-search-clear]');
    if (clearEl) {
      event.preventDefault();
      input.value = '';
      input.focus();
      resetToEmptyState();
      return;
    }

    var suggestionEl = event.target.closest('[data-suggestion-item]');
    if (suggestionEl) {
      input.value = suggestionEl.textContent.trim();
      input.focus();
      runSearch(input.value);
    }
  });

  // Optional shortcuts mentioned in the handoff: "/" or Cmd/Ctrl+K
  document.addEventListener('keydown', function (event) {
    var isSlash = event.key === '/' && !isTypingInField(event.target);
    var isCmdK = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k';
    if ((isSlash || isCmdK) && root.hidden) {
      event.preventDefault();
      open();
    }
  });

  function isTypingInField(el) {
    var tag = (el && el.tagName || '').toLowerCase();
    return tag === 'input' || tag === 'textarea' || (el && el.isContentEditable);
  }

  // ---------------------------------------------------------------------
  // Search
  // ---------------------------------------------------------------------
  input.addEventListener('input', function () {
    var query = input.value;
    clearButtons.forEach(function (btn) { btn.hidden = query.trim() === ''; });

    window.clearTimeout(debounceTimer);
    if (query.trim() === '') {
      resetToEmptyState();
      return;
    }
    debounceTimer = window.setTimeout(function () {
      runSearch(query.trim());
    }, DEBOUNCE_MS);
  });

  function resetToEmptyState() {
    clearButtons.forEach(function (btn) { btn.hidden = true; });
    productsGrid.innerHTML = emptyState.productsHTML;
    collectionsList.innerHTML = emptyState.collectionsHTML;
    helpList.innerHTML = emptyState.helpHTML;
    resultsEyebrow.textContent = emptyState.resultsEyebrowText;
    resultsLink.innerHTML = emptyState.resultsLinkHTML;
    suggestionsEyebrow.textContent = emptyState.suggestionsEyebrowText;
    suggestionsGroup.hidden = false;
    collectionsGroup.hidden = false;
    helpGroup.hidden = false;
    noResults.hidden = true;
    productsGrid.hidden = false;
  }

  function runSearch(query) {
    if (activeFetchController) activeFetchController.abort();
    activeFetchController = ('AbortController' in window) ? new AbortController() : null;

    var root_url = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var params = new URLSearchParams({
      q: query,
      'resources[type]': 'product,collection,article',
      'resources[limit]': 4,
      'resources[options][unavailable_products]': 'last',
      'resources[options][fields]': 'title,product_type,variants.title,vendor,tag'
    });

    fetch(root_url + 'search/suggest.json?' + params.toString(), {
      signal: activeFetchController ? activeFetchController.signal : undefined
    })
      .then(function (res) { return res.json(); })
      .then(function (data) { renderResults(query, data); })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        console.error('Search request failed:', err);
      });
  }

  function renderResults(query, data) {
    var results = (data && data.resources && data.resources.results) || {};
    var products = results.products || [];
    var articles = results.articles || [];

    renderProducts(query, products);
    renderSuggestions(products);
    renderCollections(query);
    renderHelp(query, articles);
  }

  function renderProducts(query, products) {
    resultsEyebrow.textContent = products.length + ' ' + (products.length === 1 ? 'PRODUCT' : 'PRODUCTS');
    resultsLink.innerHTML = 'View All →';
    resultsLink.href = (window.Shopify && window.Shopify.routes && window.Shopify.routes.search_url) ||
      ('/search?q=' + encodeURIComponent(query));

    if (!products.length) {
      productsGrid.hidden = true;
      productsGrid.innerHTML = '';
      noResults.hidden = false;
      noResultsHeading.textContent = 'Nothing for \u201c' + query + '\u201d yet.';
      // Keep collections + help visible so there is always a next step.
      return;
    }

    productsGrid.hidden = false;
    noResults.hidden = true;
    productsGrid.innerHTML = products.map(buildProductCard).join('');
  }

  function buildProductCard(product) {
    var hasSale = product.compare_at_price && parseFloat(product.compare_at_price) > parseFloat(product.price);
    var priceHTML = hasSale
      ? '<span class="search-modal__price-compare">' + escapeHTML(product.compare_at_price) + '</span>' +
        '<span class="search-modal__price-sale">' + escapeHTML(product.price) + '</span>'
      : '<span>' + escapeHTML(product.price) + '</span>';

    // Color options count calculation
    var colorCount = 0;
    if (product.options && Array.isArray(product.options)) {
      var colorOption = product.options.find(function(opt) {
        return opt.name && (opt.name.toLowerCase() === 'color' || opt.name.toLowerCase() === 'colour');
      });
      if (colorOption && colorOption.values) {
        colorCount = colorOption.values.length;
      }
    }

    var baseMeta = escapeHTML(product.type || product.vendor || '');
    var colorText = colorCount > 0 ? ' · ' + colorCount + (colorCount === 1 ? ' color' : ' colors') : '';

    var metaHTML = (baseMeta || colorText)
      ? '<p class="search-modal__product-meta">' + baseMeta + colorText + '</p>'
      : '';

    return (
      '<a href="' + escapeHTML(product.url) + '" class="search-modal__product-card">' +
        '<div class="search-modal__product-image">' +
          (product.image ? '<img src="' + escapeHTML(product.image) + '" alt="' + escapeHTML(product.title) + '" loading="lazy">' : '') +
        '</div>' +
        '<p class="search-modal__product-name">' + escapeHTML(product.title) + '</p>' +
        metaHTML +
        '<p class="search-modal__product-price">' + priceHTML + '</p>' +
      '</a>'
    );
}

  function renderSuggestions(products) {
    var titles = [];
    products.forEach(function (p) {
      if (titles.indexOf(p.title) === -1) titles.push(p.title);
    });
    titles = titles.slice(0, 6);

    suggestionsEyebrow.textContent = 'Suggestions';
    if (!titles.length) {
      suggestionsGroup.hidden = true;
      return;
    }
    suggestionsGroup.hidden = false;
    suggestionsList.innerHTML = titles.map(function (title) {
      return '<li><button type="button" class="search-modal__suggestion" data-suggestion-item>' +
        escapeHTML(title) + '</button></li>';
    }).join('');
  }

  function renderCollections(query) {
    var words = normalizeWords(query);
    var matches = collectionsIndex.filter(function (c) {
      var haystack = normalizeWords(c.title).join(' ');
      return words.every(function (w) { return haystack.indexOf(w) !== -1; });
    });

    var list = matches.length ? matches : collectionsIndex.filter(function (c) { return c.default; }).slice(0, 4);

    if (!list.length) {
      collectionsGroup.hidden = true;
      return;
    }
    collectionsGroup.hidden = false;
    collectionsList.innerHTML = list.map(function (c) {
      return (
        '<li><a href="' + escapeHTML(c.url) + '" class="search-modal__collection-row">' +
          '<span class="search-modal__collection-name">' + escapeHTML(c.title) + '</span>' +
          '<span class="search-modal__collection-count">' + escapeHTML(String(c.count)) + '</span>' +
          '<span class="search-modal__collection-arrow" aria-hidden="true">→</span>' +
        '</a></li>'
      );
    }).join('');
  }

//   function renderHelp(query) {
//     var words = normalizeWords(query);
//     var matches = helpIndex.filter(function (h) {
//       var haystack = normalizeWords(h.title + ' ' + (h.keywords || '')).join(' ');
//       return words.every(function (w) { return haystack.indexOf(w) !== -1; });
//     });

//     if (!matches.length) {
//       helpGroup.hidden = true;
//       return;
//     }
//     helpGroup.hidden = false;
//     helpList.innerHTML = matches.slice(0, 3).map(function (h) {
//       return '<li><a href="' + escapeHTML(h.url) + '" class="search-modal__help-link">' + escapeHTML(h.title) + '</a></li>';
//     }).join('');
//   }

function renderHelp(query, articles) {
  var listToRender = [];

  if (articles && articles.length > 0) {
    listToRender = articles.slice(0, 3).map(function (article) {
      return {
        title: article.title,
        url: article.url
      };
    });
  } 
  else {
    var words = normalizeWords(query);
    listToRender = helpIndex.filter(function (h) {
      var haystack = normalizeWords(h.title + ' ' + (h.keywords || '')).join(' ');
      return words.every(function (w) { return haystack.indexOf(w) !== -1; });
    }).slice(0, 3);
  }

  if (!listToRender.length) {
    helpGroup.hidden = true;
    return;
  }

  helpGroup.hidden = false;
  helpList.innerHTML = listToRender.map(function (h) {
    return '<li><a href="' + escapeHTML(h.url) + '" class="search-modal__help-link">' + escapeHTML(h.title) + '</a></li>';
  }).join('');
}

  function normalizeWords(str) {
    return (str || '').toLowerCase().trim().split(/\s+/).filter(Boolean);
  }

  function escapeHTML(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
})();